import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "@inbody/core";
import { useDepartment } from "@inbody/keycloak";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { EMPTY_VALUE } from "../constants.js";
import { UserAvatar } from "./UserAvatar.js";
import { SectionPaper } from "../../section-card/SectionPaper.js";
import { SectionHeaderBox } from "../../section-card/SectionHeaderBox.js";
import { EmptyState } from "../../empty-state/EmptyState.js";
const VISIBLE_COUNT = 50;
function UserSearchResultList(props) {
  const { filteredUserList, selectedUserId, onSelectUser } = props;
  const { t } = useTranslation("ui");
  const { gray, brand } = useColors();
  const { getLeafDepartmentName } = useDepartment();
  const [visibleCount, setVisibleCount] = useState(VISIBLE_COUNT);
  const loadMoreRef = useRef(null);
  useEffect(() => {
    setVisibleCount(VISIBLE_COUNT);
  }, [filteredUserList]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount(
            (prev) => Math.min(prev + VISIBLE_COUNT, filteredUserList.length)
          );
        }
      },
      { threshold: 1 }
    );
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => observer.disconnect();
  }, [filteredUserList.length]);
  const visibleUsers = filteredUserList.slice(0, visibleCount);
  return /* @__PURE__ */ jsxs(
    SectionPaper,
    {
      sx: {
        // 목록은 이름·소속 한 줄이면 충분하므로 좁게 잡고,
        // 남는 폭은 이메일이 잘리지 않도록 오른쪽 상세에 넘긴다.
        flex: { mobile: "1 1 auto", tablet: "0 0 260px" },
        // 세로로 쌓이는 좁은 폭에서는 목록이 화면을 다 먹지 않도록 높이를 묶어
        // 안쪽에서만 스크롤시킨다 (상세가 한참 아래로 밀리는 걸 막는다).
        maxHeight: { mobile: "38vh", tablet: "none" },
        display: "flex",
        flexDirection: "column",
        minHeight: 0
      },
      children: [
        /* @__PURE__ */ jsx(SectionHeaderBox, { children: /* @__PURE__ */ jsx(Typography, { variant: "caption", sx: { color: gray[500] }, children: t("총 {{count}}명", { count: filteredUserList.length }) }) }),
        /* @__PURE__ */ jsxs(Box, { sx: { overflowY: "auto", minHeight: 0, flex: "1 1 auto" }, children: [
          filteredUserList.length === 0 ? /* @__PURE__ */ jsx(
            EmptyState,
            {
              isForGadget: true,
              iconCircle: true,
              icon: /* @__PURE__ */ jsx(PersonSearchOutlinedIcon, { sx: { fontSize: 28 } }),
              description: t("검색 결과가 없습니다.")
            }
          ) : /* @__PURE__ */ jsx(List, { disablePadding: true, children: visibleUsers.map((user) => {
            const selected = user.id === selectedUserId;
            const leafDepartmentName = getLeafDepartmentName(
              user.leafGroupPath
            );
            return /* @__PURE__ */ jsxs(
              ListItemButton,
              {
                selected,
                onClick: () => onSelectUser(user.id),
                sx: {
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderBottom: `1px solid ${gray[50]}`,
                  "&:hover": { bgcolor: gray[25] },
                  // 고른 줄 배경은 테마 토큰으로 고정한다 (MUI 기본 선택색은 다크모드에서 흐려진다).
                  "&.Mui-selected, &.Mui-selected:hover": {
                    bgcolor: brand[25]
                  },
                  // 고른 줄은 왼쪽에 브랜드 색 띠를 세워 목록에서 바로 눈에 띄게 한다.
                  "&.Mui-selected::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    bgcolor: brand[500]
                  }
                },
                children: [
                  /* @__PURE__ */ jsx(UserAvatar, { name: user.preferredName, size: 40 }),
                  /* @__PURE__ */ jsx(
                    ListItemText,
                    {
                      primary: user.preferredName || EMPTY_VALUE,
                      secondary: [leafDepartmentName, user.jobPosition, user.jobTitle].filter(Boolean).join(" · ") || EMPTY_VALUE,
                      slotProps: {
                        primary: {
                          variant: "body2",
                          noWrap: true,
                          sx: { fontWeight: 600, color: gray[900] }
                        },
                        secondary: {
                          variant: "caption",
                          noWrap: true,
                          sx: { color: gray[500] }
                        }
                      }
                    }
                  )
                ]
              },
              user.id
            );
          }) }),
          visibleCount < filteredUserList.length && // 다음 묶음을 부르는 감지선. sx 의 height 는 0~1 숫자를 %로 읽으므로
          // 반드시 단위를 붙인다 (`1` 로 두면 패널 높이만큼 빈 칸이 생긴다).
          /* @__PURE__ */ jsx(Box, { ref: loadMoreRef, sx: { height: "1px" } })
        ] })
      ]
    }
  );
}
export {
  UserSearchResultList
};
